import { Form, Formik, FormikValues } from 'formik'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { Button } from 'components/button'
import { FieldSet } from 'components/fieldSet'
import { Flex, FlexItem } from 'components/flex'
import { MaskedInput } from 'components/maskedInput'
import { TextInput } from 'components/textInput'
import { PasswordInput } from 'components/passwordInput'
import { RadioInput } from 'components/radioInput'

import { GlobalState } from 'store'

import { formatCnpj } from 'utils/formatUtils'

import { AccountStepWrapper, InstitutionName } from './styled'
import { Representant } from 'types/enums/Representant'

type AccountStepProps = {
  initialValues: FormikValues
  validationSchema: Yup.AnySchema
  goBack: () => void
  goForward: (values: FormikValues) => void
}

export function AccountStep({
  initialValues,
  validationSchema,
  goBack,
  goForward
}: AccountStepProps): JSX.Element {
  const { t } = useTranslation(['pages'])

  const { inviteInfo } = useSelector((state: GlobalState) => state.user)

  return (
    <AccountStepWrapper>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validateOnBlur
        validateOnChange
        validateOnMount={false}
        validationSchema={validationSchema}
        onSubmit={(values) => goForward(values)}
      >
        {({ values }) => (
          <Form>
            <FieldSet
              title={t(
                'pages:subscriptionByInvite.accountStep.form.userGroup'
              ).toUpperCase()}
            >
              <Flex direction="row">
                <FlexItem grow="12">
                  <TextInput
                    label="NOME"
                    name="name"
                    placeholder={t(
                      'pages:subscriptionByInvite.accountStep.form.name'
                    )}
                  />
                </FlexItem>
              </Flex>

              <Flex direction="row" gap={6}>
                <FlexItem grow="6" basis="0">
                  <TextInput
                    label="USERNAME"
                    name="username"
                    placeholder={t(
                      'pages:subscriptionByInvite.accountStep.form.username'
                    )}
                  />
                </FlexItem>
                <FlexItem grow="6" basis="0">
                  <MaskedInput
                    label="CPF"
                    name="cpf"
                    placeholder={t(
                      'pages:subscriptionByInvite.accountStep.form.cpf'
                    )}
                    options={{
                      blocks: [3, 3, 3, 2],
                      delimiters: ['.', '.', '-'],
                      numericOnly: true
                    }}
                  />
                </FlexItem>
              </Flex>

              <TextInput
                label="EMAIL"
                isEmail={true}
                name="email"
                placeholder={t(
                  'pages:subscriptionByInvite.accountStep.form.email'
                )}
                readOnly
              />
            </FieldSet>

            <FieldSet
              title={t(
                'pages:subscriptionByInvite.accountStep.form.institutionGroup'
              ).toUpperCase()}
            >
              <InstitutionName>
                {`${inviteInfo?.institution ?? ''} - CNPJ: ${formatCnpj(
                  inviteInfo?.cnpj ?? ''
                )}`}
              </InstitutionName>

              <Flex justify="center">
                <FlexItem>
                  <RadioInput
                    name="representant"
                    options={[
                      {
                        label: 'Sou representante interno',
                        value: Representant.INNER
                      },
                      {
                        label: 'Sou representante externo',
                        value: Representant.OUTER
                      }
                    ]}
                  />
                </FlexItem>
              </Flex>

              {values.representant === Representant.OUTER && (
                <Flex direction="row" gap={6}>
                  <FlexItem grow="6" basis="0">
                    <TextInput
                      label="INSTITUIÇÃO QUE REPRESENTA"
                      name="institutionName"
                      placeholder={t(
                        'pages:subscriptionByInvite.accountStep.form.institution'
                      )}
                    />
                  </FlexItem>
                  <FlexItem grow="6" basis="0">
                    <MaskedInput
                      label="CNPJ"
                      name="institutionCnpj"
                      placeholder={t(
                        'pages:subscriptionByInvite.accountStep.form.cnpj'
                      )}
                      options={{
                        blocks: [2, 3, 3, 4, 2],
                        delimiters: ['.', '.', '/', '-'],
                        numericOnly: true
                      }}
                    />
                  </FlexItem>
                </Flex>
              )}

              <TextInput
                label="CARGO QUE REPRESENTO"
                name="jobDescription"
                placeholder={t(
                  'pages:subscriptionByInvite.accountStep.form.occupation'
                )}
              />

              <Flex direction="row" gap={6}>
                <FlexItem grow="6" basis="0">
                  <PasswordInput
                    label="SENHA"
                    name="password"
                    placeholder={t(
                      'pages:subscriptionByInvite.accountStep.form.password'
                    )}
                    autoComplete="new-password"
                  />
                </FlexItem>
                <FlexItem grow="6" basis="0">
                  <PasswordInput
                    label="CONFIRMAÇÃO DE SENHA"
                    name="passwordConfirmation"
                    type="password"
                    placeholder={t(
                      'pages:subscriptionByInvite.accountStep.form.passwordConfirmation'
                    )}
                    autoComplete="new-password"
                  />
                </FlexItem>
              </Flex>
            </FieldSet>

            <div className="container-buttons">
              <Button type="submit">
                {t('pages:subscriptionByInvite.accountStep.nextButton')}
              </Button>
              <Button onClick={() => goBack()}>Voltar</Button>
            </div>
          </Form>
        )}
      </Formik>
    </AccountStepWrapper>
  )
}
